import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useMatch, useOutletContext, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import { projectActivityLivingAccounts } from '@/components/activity/thread-living-model';
import { listActivitySubjects } from '@/lib/activity-feed';
import { sweepActivitySummary } from '@/lib/ui-editorial-sweeper';
import { cn } from '@/lib/utils';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import { accountListFilters, listDominoAccounts, readDirectoryCursor, type DirectoryPage } from '@/lib/account-directory';

type AccountList = { accounts: ActivitySubjectCard[]; directoryCount: number; loading: boolean; error: string | null; reload: () => void };

/** What an account page reads from the layout: Mon travail's read model, to show the account's tasks as rows. */
export type AccountsOutlet = AccountList & { subjects: ActivitySubjectCard[]; reloadSubjects: () => Promise<void> };

export const AccountsLayout = () => {
  const location = useLocation();
  const accountIndex = useMatch('/accounts') !== null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [subjects, setSubjects] = useState<ActivitySubjectCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [directory, setDirectory] = useState<DirectoryPage>({items:[],nextCursor:null});
  const [legacyError, setLegacyError] = useState<string | null>(null);
  const listRef = useRef<HTMLElement>(null);
  const query = (searchParams.get('q') ?? '').slice(0, 200);
  const mine = searchParams.get('mine') === '1';
  const cursorRaw = searchParams.get('ac');
  const cursor = useMemo(() => readDirectoryCursor(new URLSearchParams({q:query,...(cursorRaw ? {ac:cursorRaw} : {})})), [query,cursorRaw]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = 0; }, [query, cursorRaw]);

  useEffect(() => {
    let active = true;
    setLegacyError(null);
    void listActivitySubjects().then((response) => {
      if (active) setSubjects(response.subjects);
    }).catch(() => {
      if (active) { setSubjects([]); setLegacyError('Les travaux récents n’ont pas pu être chargés.'); }
    });
    return () => { active = false; };
  }, [reloadKey]);
  // A task moved on an account page: reload the read model without blanking the list.
  const reloadSubjects = useCallback(async () => {
    const response = await listActivitySubjects();
    setSubjects(response.subjects);
  }, []);

  useEffect(() => {
    let active = true;
    setDirectory({items:[],nextCursor:null}); setLoading(true); setError(null);
    void listDominoAccounts(query,cursor,mine).then(page => {if (active) setDirectory(page);})
      .catch(() => {if (active) setError('Les comptes n’ont pas pu être chargés.');})
      .finally(() => {if (active) setLoading(false);});
    return () => {active = false;};
  }, [query,cursor,mine,reloadKey]);

  const accounts = useMemo(() => projectActivityLivingAccounts(subjects)
    .map(({ subject }) => subject)
    .sort((left, right) => left.subjectLabel.localeCompare(right.subjectLabel, 'fr')),
  [subjects]);
  const isIndex = accountIndex;
  const accountPath = (subjectId: string) => `/threads/${encodeURIComponent(subjectId)}${accountListFilters(searchParams)}`;
  const nextSteps = useMemo(() => new Map(accounts.map(account => [account.subjectId, account.nextStepLabel])), [accounts]);

  return <div className="flex h-full min-h-0 min-w-0 flex-col bg-domino-ivory md:flex-row">
    <aside aria-label="Comptes" className={cn('flex min-h-0 w-full shrink-0 flex-col border-b border-domino-border bg-domino-warm md:w-64 md:border-b-0 md:border-r', !isIndex && 'hidden md:flex')}>
      <div className="border-b border-domino-border px-4 pb-4 pt-5">
        <label className="relative block">
          <Search aria-hidden="true" className="absolute left-3 top-3 h-4 w-4 text-domino-inkSoft" />
          <span className="sr-only">Rechercher un compte</span>
          <input value={query} maxLength={200} onChange={(event) => setSearchParams(previous => {const next = new URLSearchParams(previous);next.delete('ac');event.target.value ? next.set('q',event.target.value) : next.delete('q');return next;}, { replace: true })}
            placeholder="Rechercher un compte" className="min-h-10 w-full rounded-xl border border-domino-border bg-domino-white pl-9 pr-3 text-sm text-domino-ink focus:outline-none focus:ring-2 focus:ring-domino-prune" />
        </label>
        <div role="group" aria-label="Comptes affichés" className="mt-3 flex gap-1 text-xs">
          {([['Tous', false], ['Les miens', true]] as const).map(([label, value]) => (
            <button key={label} type="button" aria-pressed={mine === value}
              onClick={() => setSearchParams(previous => {const next = new URLSearchParams(previous);next.delete('ac');value ? next.set('mine','1') : next.delete('mine');return next;}, { replace: true })}
              className={cn('rounded-lg px-2.5 py-1', mine === value ? 'bg-domino-lilac font-medium text-domino-aubergine' : 'text-domino-inkSoft hover:bg-domino-muted')}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <nav ref={listRef} aria-label="Choisir un compte" className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
        {loading && <p role="status" className="px-3 py-4 text-sm text-domino-inkSoft">Chargement des comptes…</p>}
        {error && <div role="alert" className="px-3 py-4 text-sm text-red-700">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)} className="underline">Réessayer</button></div>}
        {!loading && !error && <>
          {directory.items.map(account => <Link key={account.subjectId} to={accountPath(account.subjectId)}
            aria-current={location.pathname === `/threads/${account.subjectId}` ? 'page' : undefined}
            className={cn('block rounded-xl px-3 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune',location.pathname === `/threads/${account.subjectId}` ? 'bg-domino-lilac text-domino-aubergine' : 'text-domino-ink hover:bg-domino-muted')}>
            <span className="block truncate text-sm font-semibold">{account.displayName}</span>
            {account.openWorkCount !== null && account.openWorkCount > 0 && <span className="mt-1 block text-xs text-domino-inkSoft">{`${account.openWorkCount} tâche${account.openWorkCount > 1 ? 's' : ''} en cours`}</span>}
            {nextSteps.get(account.subjectId) && <span className="mt-1 block truncate text-xs text-domino-inkSoft">{sweepActivitySummary(nextSteps.get(account.subjectId) ?? '')}</span>}
          </Link>)}
          {directory.items.length === 0 && <p className="px-3 py-4 text-sm text-domino-inkSoft">{query ? 'Aucun compte ne correspond à cette recherche.' : mine ? 'Aucun compte HubSpot à votre nom.' : 'Aucun compte Domino enregistré.'}</p>}
          {(cursor || directory.nextCursor) && <div className="flex flex-wrap gap-3 px-3 py-3 text-sm text-domino-aubergine">
            {cursor && <button type="button" onClick={() => setSearchParams(previous => {const next=new URLSearchParams(previous);next.delete('ac');return next;})}>Première page</button>}
            {directory.nextCursor && <button type="button" onClick={() => setSearchParams(previous => {const next=new URLSearchParams(previous);next.set('ac',JSON.stringify(directory.nextCursor));return next;})}>Comptes suivants</button>}
          </div>}
          {legacyError && <p role="alert" className="px-3 py-4 text-sm text-red-700">{legacyError} <button onClick={() => setReloadKey(v=>v+1)} className="underline">Réessayer</button></p>}
        </>}
      </nav>
    </aside>
    <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">
      <Outlet context={{ accounts, subjects, reloadSubjects, directoryCount: directory.items.length, loading, error, reload: () => setReloadKey((value) => value + 1) } satisfies AccountsOutlet} />
    </div>
  </div>;
};

export const AccountsIndex = () => {
  // The frozen lot C CRM pilot is no longer polled here (12/10/2026, audit PERF-01).
  const { accounts, directoryCount, loading, error, reload } = useOutletContext<AccountList>();
  const assistantContext = useMemo(() => ({
    surface: 'home' as const, routePath: '/accounts', routeLabel: 'Comptes',
    visibleSummary: `Liste des comptes (${Math.max(directoryCount, accounts.length)}).`,
    visibleEntities: accounts.map((account) => account.subjectLabel).slice(0, 12),
  }), [accounts, directoryCount]);
  useRegisterAppAssistantContext(assistantContext);
  if (loading) return null;
  if (error) return <div className="p-8 text-sm text-red-700">{error} <button type="button" onClick={reload} className="underline">Réessayer</button></div>;
  if (directoryCount > 0 || accounts.length > 0) return <div className="hidden p-8 text-sm text-domino-inkSoft md:block">Choisissez un compte pour reprendre son travail.</div>;
  return <div className="max-w-xl p-8"><h2 className="text-xl font-semibold text-domino-ink">Retrouver un travail</h2><p className="mt-2 text-sm text-domino-inkSoft">Les travaux plus anciens restent accessibles dans Mon travail.</p><Link to="/tasks" className="mt-5 inline-block text-sm font-medium text-domino-aubergine underline">Voir les travaux</Link></div>;
};
